import { gql } from '@apollo/client'

/**
 * @typedef {Object} PMSectionVisibilityRuleContentGraphQLResponse
 * @property {string} contentType The content type
 * @property {number} contentId The content ID
 */

/**
 * @typedef {Object} PMSectionVisibilityRuleGraphQLResponse
 * @property {string} type The visibility rule type (LOGGED_IN, ONBOARD_USER_TERMS,
 *   USER_FOLLOWING, USER_TEST_GROUP, MY_SERIES_MIN_ITEMS, FMTV_ONLY, VTS, TENURE,
 *   USER_HAS_WATCHED, FOLLOWED_SERIES_MIN_ITEMS, USER_ENTITLEMENT, UBS,
 *   CONTENT_IMPRESSION, USER_RC_TERMS, USER_VIEW_COUNT)
 * @property {boolean} [inverse] Whether to invert the rule
 * @property {Array<PMSectionVisibilityRuleContentGraphQLResponse>} [content] Array of
 *   content items (required for user-following, user-has-watched, content-impression types)
 * @property {Array<number>} [subscriptions] Array of subscription option IDs
 *   (required for user-entitlement type)
 * @property {number} [threshold] Threshold value (required for user-view-count,
 *   my-series-min-items, followed-series-min-items types)
 */

/**
 * @typedef {Object} PMSectionAppDataFooterGraphQLResponse
 * @property {string} [label] The text for the footer of the section
 */

/**
 * @typedef {Object} PMSectionAppDataHeaderGraphQLResponse
 * @property {string} [label] The text for the header of the section
 */

/**
 * @typedef {Object} PMSectionAppDataRenderGraphQLResponse
 * @property {string} [type] The render type
 * @property {string} [background] The background to render
 * @property {string} [tileCount] The display width
 * @property {string} [link] A link to the content
 */

/**
 * @typedef {Object} PMSectionAppTabContentGraphQLResponse
 * @property {string} [contentId] The content ID
 * @property {string} [contentType] The content type
 * @property {string} [label] The content label
 * @property {number} [pineScore] The recommendation score
 * @property {string} [pineSource] The source in pine for the score
 * @property {string} [videoTasteSegment] The video taste segment
 */

/**
 * @typedef {Object} PMSectionAppDataTabGraphQLResponse
 * @property {string} [tabLabel] The tab label
 * @property {Array<PMSectionAppTabContentGraphQLResponse>} tabContent The tab content
 * @property {string} [merchEventId] The merchandising event ID
 * @property {string} [viewMode] The view mode for content display
 * @property {string} [slug] The campaign slug for tracking
 * @property {string} [payloadType] The type of payload
 *   (merchandising-content, featured-content-impression, etc.)
 * @property {number} [pineScore] The recommendation score at tab level
 * @property {string} [pineSource] The source in pine for the score at tab level
 */

/**
 * @typedef {Object} PMSectionAppDataContentItemGraphQLResponse
 * @property {string} listId The list id
 * @property {string} [label] The label
 */

/**
 * @typedef {Object} PMSectionAppDataContentListGraphQLResponse
 * @property {Array<PMSectionAppDataContentItemGraphQLResponse>} [content] Array of
 *   content items with listId and label
 * @property {string} [contextType] The context for the section to be used in event reporting
 * @property {string} [displayMode] The display mode
 * @property {boolean} [enableContentPreferencesFiltering] Enable content preferences filtering
 * @property {boolean} [enableUserHiddenContent] Enable user hidden content
 * @property {boolean} [logSectionVisibility] Log section visibility
 * @property {PMSectionAppDataFooterGraphQLResponse} [footer] The footer data
 * @property {PMSectionAppDataHeaderGraphQLResponse} [header] The header data
 * @property {boolean} [hideWatched] Hide watched content
 * @property {PMSectionAppDataRenderGraphQLResponse} [render] The render data which
 *   determines how the section will be displayed
 * @property {string} [sortOrder] The sort order
 * @property {Array<PMSectionAppDataTabGraphQLResponse>} tabs The tabs data
 *   (complex computed structure)
 */

/**
 * @typedef {Object} PMSectionAppDataContinueWatchingGraphQLResponse
 * @property {string} [contextType] The context for the section to be used in event reporting
 * @property {string} [description] The description
 * @property {PMSectionAppDataHeaderGraphQLResponse} [header] The header data
 * @property {PMSectionAppDataRenderGraphQLResponse} [render] The render data which
 *   determines how the section will be displayed
 * @property {Array<PMSectionAppDataTabGraphQLResponse>} tabs The tabs data
 *   (complex computed structure)
 * @property {string} [title] The title
 */

/**
 * @typedef {Object} PMSectionAppDataPlacementGraphQLResponse
 * @property {string} [placement] The placement name
 * @property {string} [contextType] The context for the section to be used in event reporting
 * @property {Array<PMSectionAppDataTabGraphQLResponse>} tabs The tabs data
 *   (complex computed structure)
 * @property {PMSectionAppDataRenderGraphQLResponse} [render] The render data which
 *   determines how the section will be displayed
 */

/**
 * @typedef {Object} PMSectionAppDataHeaderSectionGraphQLResponse
 * @property {string} [imageId] The image id
 * @property {string} [imageUrl] The image URL (computed)
 * @property {string} [title] The title from the associated pmList
 * @property {string} [description] The description from the associated pmList
 */

/**
 * @typedef {Object} PMListItemGraphQLResponse
 * @property {string} type The content type
 * @property {string} id The content id
 * @property {string} [imageId] The image id (for legacy people lists)
 * @property {string} [imageUrl] The image URL (computed)
 * @property {string} [title] The content title (computed)
 * @property {string} [childListId] The child list id
 */

/**
 * @typedef {Object} PMListGraphQLResponse
 * @property {string} id The primary identifier for this record
 * @property {string} type List type string constant that determines what kind of
 *   content the list contains
 * @property {string} language Two letter language code (e.g., 'en', 'es', 'fr')
 *   for the list's language
 * @property {string} adminTitle Administrative title used in admin interfaces to
 *   identify this list
 * @property {string} [title] Title displayed to end users
 * @property {string} [description] Description displayed to end users
 * @property {Array<PMListItemGraphQLResponse>} listItems Array of content type/content
 *   id objects that make up this list
 * @property {string} createdAt The date the row was created
 * @property {string} updatedAt The date the row was last updated
 */

/**
 * @typedef {Object} PMSectionAppGraphQLResponse
 * @property {string} id The primary identifier for this record
 * @property {string} type Section type string constant that determines the section's
 *   behavior and data structure
 * @property {string} language Two letter language code (e.g., 'en', 'es', 'fr')
 *   for the section's language
 * @property {string} adminTitle Administrative title used in admin interfaces to
 *   identify this section
 * @property {boolean} enabled Section is hidden when not enabled
 * @property {Array<PMSectionVisibilityRuleGraphQLResponse>} [visibility] Array of
 *   visibility rule objects that control when this section is shown to users
 * @property {(
 *   PMSectionAppDataContentListGraphQLResponse|
 *   PMSectionAppDataContinueWatchingGraphQLResponse|
 *   PMSectionAppDataPlacementGraphQLResponse|
 *   PMSectionAppDataHeaderSectionGraphQLResponse
 * )} [data] Section-specific app data object with personalization and computed fields.
 *   The structure varies by section type. Union type: ContentList, ContinueWatching,
 *   Placement, or HeaderSection.
 * @property {string} [provider] The provider for the data of this section
 * @property {string} [providerKey] The key that can be used to access the data for
 *   this provider
 * @property {Array<string>} clientAttributes The client attributes to apply in
 *   combination with availability_client_attributes ordered ASC
 * @property {number} availabilityClientAttributes PM section availability of
 *   client attributes
 * @property {string} createdAt The date the row was created
 * @property {string} updatedAt The date the row was last updated
 * @property {number} [order] Section placement order within a screen
 * @property {string} [pmScreenId] The Screen this Section belongs to
 */

/**
 * @typedef {Object} PMSectionGraphQLResponse
 * @property {string} id The primary identifier for this record
 * @property {string} type Section type string constant that determines the section's
 *   behavior and data structure
 * @property {string} language Two letter language code (e.g., 'en', 'es', 'fr')
 *   for the section's language
 * @property {string} adminTitle Administrative title used in admin interfaces to
 *   identify this section
 * @property {boolean} enabled Section is hidden when not enabled
 * @property {Array<PMSectionVisibilityRuleGraphQLResponse>} [visibility] Array of
 *   visibility rule objects that control when this section is shown to users
 * @property {string} [provider] The provider for the data of this section
 * @property {string} [providerKey] The key that can be used to access the data for
 *   this provider
 * @property {Array<string>} clientAttributes The client attributes to apply in
 *   combination with availability_client_attributes ordered ASC
 * @property {number} availabilityClientAttributes PM section availability of
 *   client attributes
 * @property {string} createdAt The date the row was created
 * @property {string} updatedAt The date the row was last updated
 * @property {number} [order] Section placement order within a screen
 * @property {string} [pmScreenId] The Screen this Section belongs to
 * @property {PMSectionAppGraphQLResponse} [app] The personalized and rendered version
 *   of this section for use in applications
 * @property {Array<PMListGraphQLResponse>} [pmList] PM Lists associated with this section
 */

const GET_PM_SECTION_LIST = gql`
  query GetPmSectionList($sectionIds: [ID!]!) {
    pmSectionList(
      where: { id: { in: $sectionIds } }
      limit: 100
    ) {
      pmSections {
        id
        type
        adminTitle
        visibility {
          type
          inverse
          content {
            contentType
            contentId
          }
          subscriptions
          threshold
        }
        app {
          id
          type
          adminTitle
          enabled
          visibility {
            type
            inverse
            content {
              contentType
              contentId
            }
            subscriptions
            threshold
          }
          data {
            ... on PMSectionAppDataContentList {
              contextType
              displayMode
              enableContentPreferencesFiltering
              enableUserHiddenContent
              logSectionVisibility
              hideWatched
              sortOrder
              recommId
              footer {
                label
              }
              tabs {
                tabLabel
                merchEventId
                viewMode
                slug
                payloadType
                pineScore
                pineSource
                tabContent {
                  contentId
                  contentType
                  label
                  seedQuestion {
                    id
                    question
                    language
                    tags
                  }
                  pineSource
                  pineScore
                  videoTasteSegment
                }
              }
              header {
                label
              }
              render {
                type
                background
                tileCount
                link
              }
              content {
                listId
                label
              }
            }
            ... on PMSectionAppDataContinueWatching {
              title
              description
              header {
                label
              }
              tabs {
                tabLabel
                merchEventId
                viewMode
                slug
                payloadType
                pineScore
                pineSource
                tabContent {
                  contentId
                  contentType
                  label
                  seedQuestion {
                    id
                    question
                    language
                    tags
                  }
                  pineSource
                  pineScore
                  videoTasteSegment
                }
              }
              render {
                type
                background
                tileCount
                link
              }
            }
            ... on PMSectionAppDataPlacement {
              placement
              recommId
              contextType
              tabs {
                tabLabel
                merchEventId
                viewMode
                slug
                payloadType
                pineScore
                pineSource
                tabContent {
                  contentId
                  contentType
                  label
                  seedQuestion {
                    id
                    question
                    language
                    tags
                  }
                  pineSource
                  pineScore
                  videoTasteSegment
                  title
                  subTitle
                  description
                  mobileBackgroundUrl
                  countdown
                  eventType
                  webviewUrl
                }
              }
              render {
                type
                background
                tileCount
                link
              }
            }
            ... on PMSectionAppDataHeaderSection {
              imageId
              imageUrl
              title
              description
              asset {
                id
                renderUrl
              }
            }
          }
          provider
          providerKey
          clientAttributes
          availabilityClientAttributes
          createdAt
          updatedAt
          order
          pmScreenId
        }
        pmList(limit: 100) {
          id
          type
          language
          adminTitle
          title
          description
          listItems {
            type
            id
            imageId
            imageUrl
            title
            childListId
          }
          createdAt
          updatedAt
        }
      }
    }
  }
`

/**
 * GraphQL selection set for Content under PM tabContent rows (first-touch tiles).
 * @param {string} [languageVariable='$language'] Variable for language args, e.g.
 *   $contentLanguage when the operation uses a separate locale array.
 */
export function buildPmFirstTouchContentFields (languageVariable = '$language') {
  return `
  id
  contentId
  contentType
  language
  title
  path(format: FULL)
  shareUrl
  shortDescription(truncate: 300)
  seasonNumber
  episodeNumber
  publishedAtDate
  featureMedia {
    duration
    userMediaPosition {
      position
    }
  }
  previewMedia {
    duration
  }
  contentFeatured(language: ${languageVariable}, content: [{ publishedState: PUBLISHED_NOW }], limit: 1) {
    featuredType
    content {
      contentId
      contentType
      featureMedia {
        duration
        userMediaPosition {
          position
        }
      }
    }
  }
  imageContentRelationships(
    language: ${languageVariable}
    types: $imageType
  ) {
    type
    image {
      url
    }
  }
`
}

/**
 * Continue Watching + New For You PM sections (one section each, one tile per row).
 * Legacy query; first-touch tiles normally load via GetPmScreenPersonalized + Redux.
 */
export const CONTINUE_WATCHING_AND_NEW_FOR_YOU = gql`
  query ContinueWatchingAndNewForYou($language: [String]!, $imageType: [String]!) {
    continueWatching: pmSectionList(
      where: {
        type: { eq: "continue-watching" }
        enabled: { eq: true }
      }
      limit: 1
    ) {
      pmSections {
        id
        type
        adminTitle
        app {
          data {
            __typename
            ... on PMSectionAppDataContinueWatching {
              contextType
              tabs {
                tabLabel
                tabContent {
                  contentId
                  contentType
                  label
                  content(language: $language, limit: 1, offset: 0) {
                    ${buildPmFirstTouchContentFields('$language')}
                  }
                }
              }
            }
            ... on PMSectionAppDataContentList {
              contextType
              tabs {
                tabLabel
                tabContent {
                  contentId
                  contentType
                  label
                  content(language: $language, limit: 1, offset: 0) {
                    ${buildPmFirstTouchContentFields('$language')}
                  }
                }
              }
            }
          }
        }
      }
    }

    newForYou: pmSectionList(
      where: {
        type: { eq: "recommended-content-for-you" }
        enabled: { eq: true }
      }
      limit: 1
    ) {
      pmSections {
        id
        type
        adminTitle
        app {
          data {
            __typename
            ... on PMSectionAppDataContentList {
              contextType
              header {
                label
              }
              tabs {
                tabLabel
                tabContent {
                  contentId
                  contentType
                  label
                  content(language: $language, limit: 1, offset: 0) {
                    ${buildPmFirstTouchContentFields('$language')}
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export default GET_PM_SECTION_LIST
